import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { fmtNum } from '../utils/numerology';
import AuthModal from './AuthModal';

function renderText(text) {
  if (!text) return null;
  return text.split('\n\n').filter(p => p.trim()).map((p, i) => <p key={i}>{p.trim()}</p>);
}

export default function SharedReadingPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/readings/public/${sessionId}`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Reading not found'))
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 120 }}>
        <div className="loading-dots"><div className="dot" /><div className="dot" /><div className="dot" /></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ maxWidth: 500, margin: '100px auto', padding: '0 24px', textAlign: 'center' }} data-testid="shared-not-found">
        <div style={{ fontSize: 32, marginBottom: 16 }}>✦</div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, fontWeight: 300, marginBottom: 10 }}>Reading not found</div>
        <p style={{ color: 'var(--muted)', marginBottom: 24, fontSize: 14, lineHeight: 1.7 }}>
          This reading link might have expired or been removed. You can create your own blueprint in under a minute.
        </p>
        <button className="tool-btn" onClick={() => navigate('/tool')} data-testid="shared-go-tool">Get your blueprint →</button>
      </div>
    );
  }

  const nums = data.numbers || {};

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 80px' }} data-testid="shared-reading-page">
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <div style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 8 }}>A blueprint reading from</div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 300, color: 'var(--deep)' }}>
          <em>Little</em> Blue Numbers
        </div>
      </div>

      {/* Reading card */}
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 20, padding: '40px 32px', boxShadow: '0 2px 18px rgba(0,0,0,0.04)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontWeight: 300, color: 'var(--deep)', marginBottom: 6 }}>{data.name}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>{data.stage_label}</div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginBottom: 32 }} data-testid="shared-chips">
          {nums.star_sign && <span className="preview-chip">{nums.star_sign}</span>}
          <span className="preview-chip">Life Path {fmtNum(nums.life_path)}</span>
          <span className="preview-chip">Expression {fmtNum(nums.expression)}</span>
          <span className="preview-chip">Soul Urge {fmtNum(nums.soul_urge)}</span>
        </div>

        <div className="reading-card" style={{ border: 'none', padding: 0, boxShadow: 'none', background: 'transparent' }} data-testid="shared-preview-text">
          {renderText(data.preview)}
        </div>

        {/* Fade-out teaser */}
        <div style={{ position: 'relative', marginTop: 20, padding: '32px 24px', background: 'linear-gradient(to bottom, transparent, var(--cream-pale, #f7f3ed) 40%)', textAlign: 'center', borderRadius: 12 }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>✦</div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 300, marginBottom: 10 }}>
            There's so much more to {data.first_name}'s blueprint
          </div>
          <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 20, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>
            This is just the opening section. Create an account to read the full reading — including their Soul Urge, Shadow, Strength, and how to love them well.
          </p>
          <button
            className="tool-btn"
            onClick={() => user ? navigate('/tool') : setShowAuth(true)}
            data-testid="shared-get-full-btn"
          >
            {user ? 'Get your own blueprint →' : 'Create an account to see the full reading'}
          </button>
          <div style={{ marginTop: 14 }}>
            <button
              onClick={() => navigate('/tool')}
              style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--muted)', cursor: 'pointer', textDecoration: 'underline' }}
              data-testid="shared-get-own-link"
            >
              Or get your own blueprint instead
            </button>
          </div>
        </div>
      </div>

      {showAuth && (
        <AuthModal
          onClose={() => setShowAuth(false)}
          onSuccess={() => { setShowAuth(false); navigate('/tool'); }}
          initialTab="register"
        />
      )}
    </div>
  );
}
